import { cloneDeep, isEqual, pick } from 'lodash'

import { Colors } from '@sigma-frontend-libs/commons'
import { OrderDirections } from '@sigmasms/shared-types'

import { getOrderStorageKey, proxy } from '@/utils'

import render from './render'

/**
 * Ключи сортировки, которыми управляет кнопка.
 *
 * @type {string[]}
 */
const orderKeys = [ 'orderField', 'orderDirection' ]

/**
 * Кнопка сортировки списка с меню выбора поля и направления сортировки.
 * Значение - объект `{ orderField, orderDirection }`, каждое изменение поля или направления
 * сразу отправляется событием `input` и сохраняется в localStorage по ключу `getOrderStorageKey(service)`.
 * Кнопка сброса возвращает значение `defaultValue` и удаляет сохранённую сортировку.
 */
export default {
  name: 'ButtonOrder',

  mixins: [ proxy({ type: 'object' }) ],

  props: {
    /**
     * Поля, доступные для сортировки.
     * Каждое поле выводится пунктом выбора поля с подписью `<service>.labels.<поле>`.
     *
     * @type {string[]}
     * @default []
     */
    fields: {
      type: Array,
      default: () => {
        return []
      }
    },

    /**
     * Имя сервиса через точку в пути перевода подписей полей и в ключе сохранённой сортировки.
     *
     * @type {string}
     */
    service: {
      type: String,
      required: true
    }
  },

  computed: {
    /**
     * Пункты выбора поля сортировки.
     *
     * @returns {{ title: string, value: string }[]} Подпись и имя поля.
     */
    fieldItems() {
      return this.fields.map(field => {
        return {
          title: this.getTranslate(`${this.service}.labels.${field}`),
          value: field
        }
      })
    },

    /**
     * Кнопки выбора направления сортировки из `OrderDirections`.
     * Подпись выводится подсказкой кнопки, выбранное направление - цветом `primary`.
     *
     * @returns {{ title: string, value: string }[]} Подпись и значение направления.
     */
    directionItems() {
      return Object.values(OrderDirections).map(direction => {
        return {
          title: this.getTranslate(`order.directions.${direction}`),
          value: direction
        }
      })
    },

    /**
     * Признак совпадения `orderField` и `orderDirection` со значением по умолчанию.
     *
     * @returns {boolean} `true`, если сбрасывать нечего.
     */
    isDefault() {
      return isEqual(pick(this.proxy, orderKeys), pick(this.defaultValue, orderKeys))
    },

    /**
     * Признак выбранного поля сортировки.
     * Кнопка открытия меню с выбранным полем выводится цветом `primary`,
     * без выбранного поля кнопки направления заблокированы.
     *
     * @returns {boolean} `true`, если поле сортировки выбрано.
     */
    hasOrderField() {
      return !!this.proxy.orderField
    },

    /**
     * Ключ localStorage сохранённой сортировки сервиса.
     *
     * @returns {string} Ключ вида `sigma:<service>:order`.
     */
    storageKey() {
      return getOrderStorageKey(this.service)
    }
  },

  methods: {
    /**
     * Признак текущего направления сортировки.
     * Кнопка текущего направления выводится цветом `primary` и нажатой.
     *
     * @param {string} direction Направление сортировки из `OrderDirections`.
     * @returns {boolean} `true`, если направление совпадает с `orderDirection`.
     */
    isCurrentDirection(direction) {
      return this.proxy.orderDirection === direction
    },

    /**
     * Цвет кнопки по признаку активности.
     *
     * @param {boolean} isActive Признак активности кнопки.
     * @returns {string|undefined} `Colors.primary` для активной кнопки, `undefined` для остальных.
     */
    getActiveColor(isActive) {
      if (isActive) {
        return Colors.primary
      }
    },

    /**
     * Сохраняет `orderField` и `orderDirection` в localStorage.
     */
    saveOrder() {
      window.localStorage.setItem(this.storageKey, JSON.stringify(pick(this.proxy, orderKeys)))
    },

    /**
     * Выбирает поле сортировки и сохраняет сортировку.
     * Если направление не выбрано, выбирается первое направление из `OrderDirections`.
     *
     * @param {string} orderField Имя поля сортировки.
     */
    setOrderField(orderField) {
      this.$set(this.proxy, 'orderField', orderField)
      if (!this.proxy.orderDirection) {
        this.$set(this.proxy, 'orderDirection', this.directionItems[0].value)
      }
      this.saveOrder()
    },

    /**
     * Выбирает направление сортировки и сохраняет сортировку.
     *
     * @param {string} orderDirection Направление сортировки из `OrderDirections`.
     */
    setOrderDirection(orderDirection) {
      this.$set(this.proxy, 'orderDirection', orderDirection)
      this.saveOrder()
    },

    /**
     * Возвращает сортировку к значению по умолчанию и удаляет сохранённую сортировку.
     */
    reset() {
      this.proxy = cloneDeep(this.defaultValue)
      window.localStorage.removeItem(this.storageKey)
    }
  },

  render
}
